import { z } from 'zod/v4';

import { numberFilter, OfferPartIdsRuntype, OfferRowSchema, textFilter } from './offerPrimitives';

/** Public wire validation shared by clients and server; SQL interpretation belongs in the backend. */
function offerSearchPredicateSchema<T extends z.ZodType>(condition: T) {
    return z.union([
        condition,
        z.strictObject({ operator: z.enum(['AND', 'OR']), conditions: z.array(condition).min(1).max(2) }),
    ]);
}
const number = offerSearchPredicateSchema(numberFilter).optional();
const text = offerSearchPredicateSchema(textFilter).optional();
export const OfferSearchSortFieldSchema = z.enum([
    'createdAt',
    'id',
    'total_stock',
    'lead_time_days',
    'unit_price_1',
    'unit_price_100',
    'unit_price_1k',
    'unit_price_10k',
    'unit_price_50k',
    'origin',
    'price_type',
    'original_currency',
    'packaging',
]);
export const OfferSearchRequestSchema = z.strictObject({
    filters: z.strictObject({
        createdAt: z.strictObject({ from: z.iso.datetime(), to: z.iso.datetime() }).refine(
            ({ from, to }) => {
                const duration = Date.parse(to) - Date.parse(from);
                return duration > 0 && duration <= 366 * 86400000;
            },
            { message: 'Observation window must be positive and at most 366 days' },
        ),
        partIds: OfferPartIdsRuntype.optional(),
        componentIds: z.array(z.string().min(1).max(500)).min(1).max(50).optional(),
        supplierIds: z.array(z.uuid()).min(1).max(300).optional(),
        supplierPolicy: z.literal('approved').optional(),
        total_stock: number,
        lead_time_days: number,
        unit_price_1: number,
        unit_price_100: number,
        unit_price_1k: number,
        unit_price_10k: number,
        unit_price_50k: number,
        origin: text,
        price_type: text,
        original_currency: text,
        packaging: text,
        validity: text,
        classification_rule: text,
    }),
    sort: z
        .strictObject({ field: OfferSearchSortFieldSchema, direction: z.enum(['asc', 'desc']) })
        .default({ field: 'createdAt', direction: 'desc' }),
    groupBy: z.enum(['supplier', 'stockLocation']).optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export type OfferSearchRequest = z.input<typeof OfferSearchRequestSchema>;
export const OfferSearchRowSchema = OfferRowSchema.extend({
    componentId: z.string().nullable(),
    inventorySiteId: z.uuid().nullable(),
    partId: z.uuid().nullable(),
    manufacturerId: z.uuid().nullable(),
    supplierId: z.uuid().nullable(),
    stockLocationId: z.uuid().nullable(),
});
export type OfferSearchRow = z.infer<typeof OfferSearchRowSchema>;
export const OfferSearchResultSchema = z.object({ rows: z.array(OfferSearchRowSchema), hasMore: z.boolean() });
export type OfferSearchResult = z.infer<typeof OfferSearchResultSchema>;
